import { type FC, useEffect, useRef } from 'react'

import type { OptaTeamStanding } from '@sportworld/js-sdk/src/types/soccerDataTypes'
import { StandingsTypes } from '@sportworld/js-sdk/src/types/soccerDataTypes'

import { getPagePopupByTeamId } from '@/actions/actions'
import { getStandings } from '@/actions/soccerData'
import { ContentUnavailable } from '@/components/ContentUnavailable/ContentUnavailable'
import { Loader } from '@/components/Loader'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import useStore from '@/store/useStore'

import { StandingsGroups } from './StandingsGroups'
import { StandingsLeague } from './StandingsLeague'
import { ContentUnavailableWrapper } from '../styles'
import { type StandingsProps } from '../types'

const standingsComponents = {
  [StandingsTypes.LEAGUE]: StandingsLeague,
  [StandingsTypes.GROUPS]: StandingsGroups,
}

export const Standings: FC<StandingsProps> = ({
  isPopup,
  tournamentCalendarId,
}) => {
  const { t } = useCommonTranslation()
  const controller = useRef<Nullable<AbortController>>()
  const {
    division: divisionPage,
    divisionPopup,
    loading,
    loadingPopup,
    setDivision,
    setTournamentCalendar,
  } = useStore((state) => state.soccerData)

  const isLoading = isPopup ? loadingPopup.standings : loading.standings
  const currentDivision = isPopup ? divisionPopup.division : divisionPage
  const standingsType =
    currentDivision.length > 1 ? StandingsTypes.GROUPS : StandingsTypes.LEAGUE

  const StandingsComponent = standingsComponents[standingsType]

  const handleClickTeam = (team: OptaTeamStanding, focusId: string) => {
    controller.current?.abort()
    controller.current = new AbortController()
    void getPagePopupByTeamId(
      team,
      tournamentCalendarId,
      controller.current.signal,
      { focusId },
    )
  }

  useEffect(() => {
    const controller = new AbortController()
    const abortSignal = controller.signal

    void getStandings(isPopup, tournamentCalendarId, abortSignal)

    return () => {
      controller.abort()
      !isPopup && setDivision([])
      setTournamentCalendar(null)
    }
  }, [isPopup, tournamentCalendarId])

  if (isLoading && !currentDivision.length) {
    return <Loader />
  }

  if (!isLoading && !currentDivision.length) {
    return (
      <ContentUnavailableWrapper>
        <ContentUnavailable>
          {t('soccerData.standings.noData.text')}
        </ContentUnavailable>
      </ContentUnavailableWrapper>
    )
  }

  return <StandingsComponent isPopup={isPopup} onClickTeam={handleClickTeam} />
}
